import React, { useContext, useEffect, useState } from "react";
import CommonModal from "../CommonModal";
import CustomTable from "../../CommonComponents/Table/CustomTable";
import { networkService } from "../../config/axios";
import DeleteOrgModal from "./DeleteOrgModal";
import UserInfoContext from "../../../utils/userInfoContext";
import { AdminActionIcons, AdminModalFooter } from "../../Admin/Shared";
import "../../Admin/Shared/AdminModal.css";

/**
 * Modal: Assign Domains to a SINGLE User
 */
const UserDomainsModal = ({ openModal, closeModal, user }) => {
    const { setShowNotifications } = useContext(UserInfoContext);

    const [show, setShow] = useState("list");
    const [userDomains, setUserDomains] = useState([]);

    const [editingId, setEditingId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    /* -------- AUTOCOMPLETE STATES -------- */
    const [domainSearch, setDomainSearch] = useState("");
    const [domainResults, setDomainResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState(null);

    /* -------- FORM STATE -------- */
    const [formData, setFormData] = useState({
        user_id: user?.id,
        default: false,
    });

    /* ---------------- Fetch Data ---------------- */
    useEffect(() => {
        if (!openModal) return;

        setFormData((prev) => ({
            ...prev,
            user_id: user?.id,
        }));

        fetchUserDomains();
    }, [openModal, show, deleteId, user?.id]);

    const fetchUserDomains = async () => {
        const res = await networkService(
            `/admin/user_domains.json?user_id=${user?.id}`,
            "GET"
        );
        setUserDomains(res?.data || []);
    };

    /* ---------------- Domain Search ---------------- */
    const searchDomains = async (value) => {
        if (!value || value.length < 2) {
            setDomainResults([]);
            return;
        }

        const res = await networkService(
            `/admin/master_domains.json?query=${value}`,
            "GET"
        );

        setDomainResults(res?.data?.master_domains || []);
    };

    /* ---------------- Submit ---------------- */
    // A user's only domain is always their default (matches the server-side
    // rule), so the checkbox is forced on and locked in that case.
    const mustBeDefault = editingId
        ? userDomains.length === 1
        : userDomains.length === 0;

    // Editing the current default while other domains exist: the checkbox stays
    // checked and unchecking it warns instead — a default must always exist, so
    // it can only be moved by setting another domain as default.
    const isEditingCurrentDefault = !!userDomains.find(
        (d) => d.id === editingId
    )?.default;

    const handleSubmit = async () => {
        try {
            const payload = {
                user_domain: {
                    user_id: user?.id,
                    default: mustBeDefault || isEditingCurrentDefault || formData.default,
                    ...(selectedDomainId
                            ? { master_domain_id: selectedDomainId }
                            : { domain_name: domainSearch.trim() }
                    )
                }
            };

            if (editingId) {
                await networkService(
                    `/admin/user_domains/${editingId}.json`,
                    "PUT",
                    { user_domain: { default: mustBeDefault || formData.default } }
                );

                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "User domain updated",
                });
            } else {
                await networkService(
                    "/admin/user_domains.json",
                    "POST",
                    payload
                );

                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Domain assigned to user",
                });
            }

            resetForm();
            setShow("list");
            fetchUserDomains();
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage:
                    error?.response?.data?.errors?.join(", ") ||
                    "Something went wrong",
            });
        }
    };

    /* ---------------- Delete ---------------- */
    const confirmDelete = async () => {
        try {
            await networkService(
                `/admin/user_domains/${deleteId}.json`,
                "DELETE"
            );

            setDeleteId(null);
            setShowNotifications({
                showToast: true,
                toastType: "success",
                toastMessage: "Domain removed from user",
            });
            fetchUserDomains();
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage:
                    error?.response?.data?.errors?.join(", ") ||
                    "Failed to delete",
            });
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setSelectedDomainId(null);
        setDomainSearch("");
        setDomainResults([]);
        setShowResults(false);

        setFormData({
            user_id: user?.id,
            default: false,
        });
    };

    /* ---------------- Table Columns ---------------- */
    const columns = [
        {
            Header: "Domain",
            accessor: row => row.master_domain?.name,
        },
        {
            Header: "Default",
            accessor: "default",
            Cell: ({ value }) => (value ? "Yes" : "No"),
        },
        {
            Header: "Actions",
            accessor: "action",
            Cell: ({ row: { original } }) => (
                <AdminActionIcons
                    itemName={original.master_domain?.name ?? "domain"}
                    onEdit={() => {
                        setEditingId(original.id);
                        setDomainSearch(original.master_domain?.name);
                        setSelectedDomainId(original.master_domain_id);
                        setFormData({
                            user_id: user?.id,
                            default: original.default,
                        });
                        setShow("form");
                    }}
                    onDelete={() => setDeleteId(original.id)}
                />
            ),
        },
    ];

    return (
        <>
            <DeleteOrgModal
                title="Are you sure?"
                openModal={deleteId !== null}
                closeModal={() => setDeleteId(null)}
                name="remove this domain from user"
                handleDelete={confirmDelete}
            />

            <CommonModal
                title={`Manage ${user?.name} Domains`}
                modalStatus={openModal}
                changeActiveStatus={closeModal}
                classList="admin-modal admin-user-domains-modal"
                centered
                showFooter={false}
            >
                {show === "list" ? (
                    <div className="domain-list-view">
                        <div className="admin-table-wrapper">
                            <CustomTable
                                data={userDomains}
                                columnData={columns}
                                showFilters={false}
                            />
                        </div>
                        <div className="admin-list-footer">
                            <button
                                type="button"
                                className="admin-cta-button"
                                onClick={() => {
                                    resetForm();
                                    setShow("form");
                                }}
                            >
                                + ADD Domain
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="domain-form-container">
                        <div className="admin-form-group">
                            <label htmlFor="user-domain-name" className="admin-form-label">
                                Domain Name
                            </label>
                            <div className="domain-input-wrapper domain-input-wrapper-autocomplete">
                                <input
                                    id="user-domain-name"
                                    type="text"
                                    className="domain-input"
                                    placeholder="Enter domain name (example.com)"
                                    value={domainSearch}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setDomainSearch(value);
                                        setSelectedDomainId(null);
                                        setShowResults(true);
                                        searchDomains(value);
                                    }}
                                    onBlur={() =>
                                        setTimeout(() => setShowResults(false), 200)
                                    }
                                />
                                {showResults && domainResults.length > 0 && (
                                    <ul className="domain-autocomplete-list">
                                        {domainResults.map((domain) => (
                                            <li
                                                key={domain.id}
                                                className="domain-autocomplete-item"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setDomainSearch(domain.name);
                                                    setSelectedDomainId(domain.id);
                                                    setShowResults(false);
                                                }}
                                            >
                                                {domain.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <label className="admin-form-checkbox-label">
                            <input
                                type="checkbox"
                                checked={mustBeDefault || isEditingCurrentDefault || formData.default}
                                disabled={mustBeDefault}
                                onChange={() => {
                                    if (isEditingCurrentDefault) {
                                        setShowNotifications({
                                            showToast: true,
                                            toastType: "danger",
                                            toastMessage:
                                                "You can't disable the default domain. Set another domain as the default instead.",
                                        });
                                        return;
                                    }
                                    setFormData({
                                        ...formData,
                                        default: !formData.default,
                                    });
                                }}
                            />
                            Set as Default Domain
                            {(mustBeDefault || isEditingCurrentDefault) && (
                                <span className="admin-form-checkbox-hint">
                                    {mustBeDefault
                                        ? "(a user's only domain is always their default)"
                                        : "(set another domain as default to change this)"}
                                </span>
                            )}
                        </label>

                        <AdminModalFooter
                            onCancel={() => {
                                resetForm();
                                setShow("list");
                            }}
                            onSubmit={handleSubmit}
                            cancelText="Cancel"
                            submitText={editingId ? "Update" : "Assign"}
                            submitType="button"
                            submitDisabled={!domainSearch.trim()}
                        />
                    </div>
                )}
            </CommonModal>
        </>
    );
};

export default UserDomainsModal;
